import { Alert, Button, Col, Container, Form, Modal, Row, Table } from "react-bootstrap"
import InputBox from "../../../components/InputBox/InputBox";
import { useMemo, useState } from "react";
import { AviTracerApi } from "../../../avitracerApi";
import { useAppDispatch } from "../../../store/hooks";
import { useNavigate } from "react-router-dom";
import { createAircraft, createAircraftFromShared, updateAircraft } from "../../../store/aircraftSlice";
import { FplImportedAircraft, parseAircraftFromFpl } from "../../../utils/FplImportUtils";

function AddAircraftPage() {

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [registration, setRegistration] = useState<string>("");
  const [aircraftIcaoType, setAircraftIcaoType] = useState<string>();
  const [isChecking, setIsChecking] = useState<boolean>(false);
  const [error, setError] = useState<string>();

  const [isCreating, setIsCreating] = useState<boolean>(false)

  //"import" is only reachable once the registration was checked and is new to BRIEF
  const [step, setStep] = useState<"registration" | "import">("registration");
  const [fplText, setFplText] = useState<string>("");

  const fplParse = useMemo((): { imported?: FplImportedAircraft; error?: string } => {
    if (fplText.trim().length === 0) {
      return {};
    }
    try {
      return { imported: parseAircraftFromFpl(fplText) };
    } catch (e: any) {
      return { error: e.message };
    }
  }, [fplText]);

  const checkAircraftRegistration = async () => {
    setIsChecking(true);
    try {
      const { icaoType } = await AviTracerApi.getAircraftInfoByRegistration(registration);
      setAircraftIcaoType(icaoType);
    } catch (error: any) {
      setError(error);
    } finally {
      setIsChecking(false);
    }
  };

  const createAircraftFromSharedAction = async () => {
    setIsCreating(true);
    try {
      const aircraft = await dispatch(createAircraftFromShared(registration)).unwrap();
      navigate(`/aircraft/${aircraft.id}`);
    } catch (error: any) {
      setError(error.message);
    }finally {
      setIsCreating(false);
    }
  };

  const createNewAircraft = async (fplImport?: FplImportedAircraft) => {
    setIsCreating(true);
    try {
      var aircraft = await dispatch(createAircraft(registration)).unwrap();
      if (fplImport) {
        //A new aircraft is editable by the org that created it, so the imported values go straight to the shared aircraft
        aircraft = await dispatch(updateAircraft({ ...aircraft, ...fplImport.aircraft })).unwrap();
      }
      navigate(`/aircraft/${aircraft.id}`);
    } catch (error: any) {
      setStep("registration");
      setError(error.message);
    } finally {
      setIsCreating(false);
    }
  }

  return (
    <Modal show size="lg">
      <Modal.Body>
        {step === "registration" ? (
          <Container>
            <Row className="align-items-center">
              <Col>
                <h1>Add Aircraft</h1>
              </Col>
            </Row>
            <Row className="pt-2">
              <Col>
                <div>Please enter your aircraft registration.</div>
                <small>
                  If the aircraft is already in our BRIEF system, it will be automatically imported into your account. Otherwise, you can
                  create it yourself or import its details from a previous flight plan.
                </small>
              </Col>
            </Row>
            <Row className="pt-4">
              <Col>
                <InputBox
                  value={registration}
                  isDisabled={isChecking || isCreating}
                  name={"Registration (ex. SX-DEMO or D-ABCD)"}
                  uppercase={true}
                  type={"text"}
                  onChange={(v) => {
                    setError(undefined);
                    setAircraftIcaoType(undefined);
                    setRegistration(v);
                  }}
                />
              </Col>
            </Row>

            <Row className="pt-4 justify-content-center">
              {error === "NOT_FOUND" ? (
                <>
                  <Col xs={12}>
                    <Alert variant="secondary">
                      <strong>{registration} is not in BRIEF yet.</strong> How would you like to create it?
                    </Alert>
                  </Col>
                  <Col xs={12} md={6} className="pb-2">
                    <Button variant="outline-dark" className="w-100 h-100 text-start p-3" disabled={isCreating} onClick={() => createNewAircraft()}>
                      <strong>{isCreating ? "Creating..." : "Create blank aircraft"}</strong>
                      <br />
                      <small>Fill in its details yourself on the next page.</small>
                    </Button>
                  </Col>
                  <Col xs={12} md={6} className="pb-2">
                    <Button variant="dark" className="w-100 h-100 text-start p-3" disabled={isCreating} onClick={() => setStep("import")}>
                      <strong>Import from a flight plan</strong>
                      <br />
                      <small>Copy type, equipment, colour etc. from a filed FPL message.</small>
                    </Button>
                  </Col>
                </>
              ) : error ? (
                <Col xs={12}>
                  <Alert variant="danger">{error}</Alert>
                </Col>
              ) : aircraftIcaoType ? (
                <Col xs={12}>
                  <Alert variant="success">
                    <strong>Aircraft found in BRIEF!</strong>
                    <br />
                    {registration} is set as a <strong>{aircraftIcaoType}</strong> in our database.
                    <br />
                    If the above info are wrong, please contact us at <a href="mailto:support@avitracer.com">support@avitracer.com</a>.
                  </Alert>
                </Col>
              ) : (
                <Col xs={"auto"}>
                  <Button disabled={isChecking || registration.length === 0} variant="dark" onClick={checkAircraftRegistration}>
                    {isChecking ? "Checking..." : "Check Registration"}
                  </Button>
                </Col>
              )}
            </Row>
          </Container>
        ) : (
          <Container>
            <Row>
              <Col>
                <h1>Import from a flight plan</h1>
                <div>Paste a filed FPL message. Its details will be copied to {registration}.</div>
                <small>
                  The ICAO FPL Code of a BRIEF flight plan or a whole filing confirmation email both work. Only the (FPL-...) part is read.
                </small>
              </Col>
            </Row>
            <Row className="pt-3">
              <Col>
                <Form.Control
                  as="textarea"
                  rows={8}
                  autoFocus
                  value={fplText}
                  disabled={isCreating}
                  placeholder={"(FPL-SXABC-VG\n-C172/L-SDFGY/S\n-LGAV1200\n-N0110VFR DCT\n-LGTS0100\n-DOF/261016\n-E/0300 P/2 R/VE A/WHITE RED C/DOE)"}
                  style={{ fontFamily: "monospace", fontSize: "0.85rem" }}
                  onChange={(e) => setFplText(e.target.value)}
                />
              </Col>
            </Row>
            {fplParse.error && (
              <Row className="pt-3">
                <Col>
                  <Alert variant="warning" className="mb-0">
                    {fplParse.error}
                  </Alert>
                </Col>
              </Row>
            )}
            {fplParse.imported && (
              <Row className="pt-3">
                <Col>
                  <FplDecodedTable imported={fplParse.imported} registration={registration} />
                </Col>
              </Row>
            )}
          </Container>
        )}

        <hr />
        <Container className="">
          <Row className="justify-content-center">
            {step === "registration" ? (
              <>
                <Col xs={"4"}>
                  <Button variant="light" className="w-100 border" onClick={() => navigate("/aircraft")}>
                    Cancel
                  </Button>
                </Col>
                {aircraftIcaoType && !error && (
                  <Col xs={"4"}>
                    <Button disabled={isChecking || isCreating} variant="dark" className="w-100 " onClick={createAircraftFromSharedAction}>
                      {isCreating ? "Adding..." : "Add to my aircraft"}
                    </Button>
                  </Col>
                )}
              </>
            ) : (
              <>
                <Col xs={"4"}>
                  <Button variant="light" className="w-100 border" disabled={isCreating} onClick={() => setStep("registration")}>
                    Back
                  </Button>
                </Col>
                <Col xs={"4"}>
                  <Button
                    disabled={isCreating || !fplParse.imported}
                    variant="dark"
                    className="w-100 "
                    onClick={() => createNewAircraft(fplParse.imported)}
                  >
                    {isCreating ? "Creating..." : `Create ${registration}`}
                  </Button>
                </Col>
              </>
            )}
          </Row>
        </Container>
      </Modal.Body>
    </Modal>
  );
}

function FplDecodedTable({ imported, registration }: { imported: FplImportedAircraft; registration: string }) {
  const a = imported.aircraft;
  const sar = a.sarEquipment!;

  const flags = (pairs: [boolean, string][]) => pairs.filter(([on]) => on).map(([, label]) => label).join(", ");

  const rows: [string, string, string | undefined][] = [
    ["9", "Aircraft type", a.icaoType],
    ["9", "Wake turbulence", a.wakeTurbulenceCategory],
    ["10a", "Equipment", a.equipmentCodes + (a.is833Capable ? " (8.33 kHz)" : "") + (a.isRvsmApproved ? " (RVSM)" : "")],
    ["10b", "Surveillance", a.surveillanceEquipmentCodes],
    ["15", "Cruise speed", a.cruiseSpeed && `${a.cruiseSpeed.value} ${a.cruiseSpeed.unit === "N" ? "kt" : "km/h"}`],
    ["18", "PBN/", a.pbnCapabilities],
    ["18", "NAV/", a.field18Nav],
    ["18", "COM/", a.field18Com],
    ["18", "DAT/", a.field18Dat],
    ["18", "Operator (OPR/)", a.operatorFullName],
    ["18", "Remarks (RMK/)", a.field18Remarks],
    ["19", "Emergency radio", flags([[sar.emergencyRadio.elba, "ELT"], [sar.emergencyRadio.vhf, "VHF"], [sar.emergencyRadio.uhf, "UHF"]]) || "None"],
    ["19", "Survival equipment", sar.survivalEquipment.general ? flags([[sar.survivalEquipment.polar, "Polar"], [sar.survivalEquipment.desert, "Desert"], [sar.survivalEquipment.maritime, "Maritime"], [sar.survivalEquipment.jungle, "Jungle"]]) || "Yes" : undefined],
    ["19", "Life jackets", sar.lifejackets.general ? flags([[sar.lifejackets.light, "Light"], [sar.lifejackets.fluores, "Fluorescein"], [sar.lifejackets.uhf, "UHF"], [sar.lifejackets.vhf, "VHF"]]) || "Yes" : undefined],
    ["19", "Dinghies", sar.dinghies.number > 0 ? `${sar.dinghies.number} × ${sar.dinghies.capacity} persons${sar.dinghies.cover ? ", covered" : ""}${sar.dinghies.colour ? `, ${sar.dinghies.colour}` : ""}` : undefined],
    ["19", "Aircraft colour", sar.aircraftColour],
    ["19", "SAR remarks (N/)", sar.remarks],
  ];

  const isOtherAircraft = imported.registration.replace("-", "") !== registration.replace("-", "");

  return (
    <>
      {isOtherAircraft && (
        <Alert variant="info" className="py-2">
          This flight plan is for <strong>{imported.registration}</strong>. Its details will be copied to <strong>{registration}</strong>.
        </Alert>
      )}
      <div className="pb-2" style={{ fontFamily: "monospace", fontSize: "0.8rem", wordBreak: "break-all" }}>
        {imported.message}
      </div>
      <Table size="sm" bordered className="mb-1">
        <thead>
          <tr>
            <th style={{ width: "4rem" }}>Item</th>
            <th>Field</th>
            <th>Will be saved as</th>
          </tr>
        </thead>
        <tbody>
          {rows
            .filter(([, , value]) => value)
            .map(([item, label, value]) => (
              <tr key={label}>
                <td className="text-muted">{item}</td>
                <td>{label}</td>
                <td>
                  <strong>{value}</strong>
                </td>
              </tr>
            ))}
        </tbody>
      </Table>
      <small className="text-muted">
        Not copied, as they change per flight: route, times, endurance (E/), persons on board (P/) and PIC (C/). You can review everything on
        the next page.
      </small>
    </>
  );
}

export default AddAircraftPage;
